import { Loading, useLazyDataQuery } from "@lyfegen-platform/react-common";
import { Button, IconButton, useTheme } from "@material-ui/core";
import { Spacing } from "@material-ui/core/styles/createSpacing";
import { Delete } from "@material-ui/icons";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { TFunction, Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useSetRecoilState } from "recoil";
import LyfeTooltip from "../../../components/LyfeTooltip";
import ModalDialog from "../../../components/ModalDialog";
import { useDataMutation } from "../../../hooks/useDataModule";
import { SnackBarMessageType } from "../../../models/snackbarMessageType";
import { snackbarState } from "../../../store/layout";
import { CAN_DELETE_FLEX_ENTITIES } from "../queries";
import { DELETE_FLEX_ENTITY } from "./queries";
import { GetCaseByIdQuery } from "../../cases/types/queries.generated";
import { CanDeleteFlexEntitiesQuery } from "../types/queries.generated";
import { ErrorImpactDialog, getErrorImpact } from "../../../components/flexible/FlexEntityErrorImpactDialog";
import { DeleteFlexEntityMutation } from "./types/queries.generated";

export interface IDeleteFormProps {
	id?: string;
	caseId?: string;
	onDelete?: () => void;
	caseCount?: number;
	caseNonStartingPointForms?: Array<GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]>;
	isIcon?: boolean;
	isStartingPoint?: boolean;
	setIsButtonDisabled?: Dispatch<SetStateAction<boolean>>;
	isButtonDisabled?: boolean;
}

const entityLockedError = "entity-locked";

const DeleteForm = (props: IDeleteFormProps) => {
	const { id, caseId, onDelete, caseCount, isStartingPoint } = props;
	const { t } = useTranslation();
	const { push } = useHistory();
	const { spacing } = useTheme();
	const [showDialog, setShowDialog] = useState(false);
	const [errorImpact, setErrorImpact] = useState<CanDeleteFlexEntitiesQuery["CanDeleteFlexEntities"]["impacts"][number] | null>(null);
	const [isCaseDeletionAllowed, setIsCaseDeletionAllowed] = useState(false);
	const setSnackBar = useSetRecoilState(snackbarState);
	const [canDeleteFlexEntity, setCanDeleteFlexEntity] = useState<boolean | null>(null);

	const openDialog = () => setShowDialog(true);
	const closeDialog = () => setShowDialog(false);

	const [loadCanDelete, { loading }] = useLazyDataQuery<CanDeleteFlexEntitiesQuery>(CAN_DELETE_FLEX_ENTITIES, {
		variables: {
			ids: [id],
		},
		fetchPolicy: "no-cache",
		onCompleted: (data) => {
			const deletable = data?.CanDeleteFlexEntities?.deletableResult[0]?.deletable ?? false;
			const errorImpact = getErrorImpact(data?.CanDeleteFlexEntities?.impacts || []);
			setErrorImpact(errorImpact);
			setCanDeleteFlexEntity(deletable);
			if (deletable && !errorImpact) {
				openDialog();
			}
		},
	});

	useEffect(() => {
		props.setIsButtonDisabled?.(loading);
	}, [loading]);

	const getCaseText = (isStartingPoint: boolean, caseCount: number | undefined, t: TFunction) => {
		if (!isStartingPoint) return `0 ${t("cases-singular").toLowerCase()}`;

		return caseCount && caseCount > 1
			? `${caseCount} ${t("cases").toLowerCase()}`
			: `${caseCount} ${t("cases-singular").toLowerCase()}`;
	};

	const handleErrorImpactClose = () => {
		setErrorImpact(null);
		closeDialog();
	};

	const handleCaseDeletionAllowed = () => {
		setIsCaseDeletionAllowed(true);
		handleDelete();
		setErrorImpact(null);
	};

	const getAdditionalFormsTextArray = (
		forms: Array<GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]> | undefined,
		t: TFunction
	) => {
		if (!forms?.length || !isStartingPoint || !canDeleteFlexEntity) return "";

		return [t("following-forms-will-be-deleted"), ...forms.map((form) => ` ${form.node.id}`)];
	};

	const [deleteForm, { loading: saving }] = useDataMutation<DeleteFlexEntityMutation>(DELETE_FLEX_ENTITY, {
		onCompleted: () => {
			if (!caseId) {
				push("/data");
			} else if (isStartingPoint) {
				push("/cases");
			} else {
				window.location.reload();
			}

			setSnackBar({
				isOpen: true,
				message: (
					<Trans i18nKey={caseCount !== 0 ? "deleted-success-with-cases" : "deleted-success"}>
						{{ formId: id, caseCount: getCaseText(!!isStartingPoint, caseCount, t) }}{" "}
					</Trans>
				),
				type: SnackBarMessageType.Success,
				duration: 5000,
			});
		},
		onError: (error) => {
			console.error(error.message);
			if (error.message === entityLockedError) {
				setSnackBar({
					isOpen: true,
					message: t("entity-locked"),
					type: SnackBarMessageType.Error,
					duration: 5000,
				});
			}
		},
	});

	const handleDelete = () => {
		onDelete && onDelete();
		deleteForm({ variables: { id } });
	};

	if (!props.id) {
		return null;
	}

	const dialogText = {
		continue: t("delete-continue"),
		discard: t("delete-discard"),
		question: `${t("delete-confirmation-form")}`,
		reason: getAdditionalFormsTextArray(props.caseNonStartingPointForms, t),
	};

	const handleDeleteClick = () => {
		loadCanDelete();
	};

	return (
		<>
			<ModalDialog isOpen={showDialog} onContinueClick={handleDelete} onDiscardClick={closeDialog} text={dialogText} />
			<ErrorImpactDialog
				errorImpact={errorImpact}
				isCaseDeletionAllowed={isCaseDeletionAllowed}
				onAllowCaseDeletion={handleCaseDeletionAllowed}
				onDiscardChange={handleErrorImpactClose}
				onClose={handleErrorImpactClose}
			/>
			<ButtonOrIcon
				spacing={spacing}
				onClick={handleDeleteClick}
				disabled={canDeleteFlexEntity === false}
				isLocked={errorImpact?.kind === "entity-locked"}
				isIcon={props.isIcon}
				loading={loading || saving}
				isDeletionOccurring={props.isButtonDisabled}
				t={t}
			/>
		</>
	);
};

const ButtonOrIcon = (props: {
	isIcon?: boolean;
	spacing: Spacing;
	disabled?: boolean;
	isLocked?: boolean;
	loading?: boolean;
	isDeletionOccurring?: boolean;
	onClick: () => void;
	t: TFunction;
}) => {
	const { t } = props;

	const getTooltip = () => {
		if (props.isLocked) return t("entity-locked");
		if (props.disabled) return t("form-deletion-not-possible-tooltip");

		return t("delete-form-if-possible-tooltip");
	};

	return (
		<LyfeTooltip title={getTooltip()}>
			<span>
				{props.isIcon ? (
					<IconButton onClick={props.onClick} color="primary" disabled={props.disabled || props.isDeletionOccurring}>
						{props.loading ? <Loading size={20} /> : <Delete fontSize="small" />}
					</IconButton>
				) : (
					<Button
						variant="contained"
						color="primary"
						style={{ minWidth: props.spacing(15), marginLeft: props.spacing(2) }}
						onClick={props.onClick}
						disabled={props.disabled || props.loading || props.isDeletionOccurring}
					>
						{props.loading ? <Loading size={20} /> : t("delete")}
					</Button>
				)}
			</span>
		</LyfeTooltip>
	);
};

export default DeleteForm;
